import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard, { WidgetSuccessState } from "./WidgetCard";

const MissingMonthlyReportsWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchMissingReports = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const response = await secureAxios.get("/api/widgets/missing-reports", {
          params: {
            only_my_data: onlyMyData,
          },
        });
        setData(response.data || []);
        setError(false);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching missing monthly reports");
        }
        setError(true);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMissingReports();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  const handleCreateReport = (authId, consumerId) => {
    navigate(
      `/crm/monthly-reports/create?authorization_id=${authId}&consumer_id=${consumerId}`
    );
  };

  const lastMonth = new Date(
    new Date().setMonth(new Date().getMonth() - 1)
  ).toLocaleString("default", { month: "long", year: "numeric" });

  // Show success state when no missing reports
  if (!loading && !error && data.length === 0) {
    return (
      <WidgetCard
        title="Monthly Reports"
        icon="bi-file-earmark-text"
        iconColor="success"
        loading={loading}
      >
        <WidgetSuccessState
          message={`All reports for ${lastMonth} have been submitted.`}
        />
      </WidgetCard>
    );
  }

  const subtitle = `Reports needed for ${lastMonth}${
    onlyMyData ? " (my consumers only)" : ""
  }`;

  return (
    <WidgetCard
      title="Missing Monthly Reports"
      subtitle={!loading && !error ? subtitle : undefined}
      icon="bi-file-earmark-x"
      iconColor="danger"
      loading={loading}
      error={error}
      errorMessage="Unable to load missing reports"
      badge={data.length > 0 ? data.length : undefined}
      badgeColor="danger"
    >
      {/* Desktop/tablet table view */}
      <div className="d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-sm table-bordered widget-table">
            <thead>
              <tr>
                <th>Consumer</th>
                <th>Authorization</th>
                <th>Type</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((auth) => (
                <tr key={auth.authorization_id}>
                  <td className="widget-consumer-name">{auth.consumer_name}</td>
                  <td>
                    <small className="text-muted">
                      {auth.authorization_number}
                    </small>
                  </td>
                  <td>
                    <span className="text-capitalize">{auth.auth_type}</span>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() =>
                        handleCreateReport(
                          auth.authorization_id,
                          auth.consumer_id
                        )
                      }
                      aria-label={`Create report for ${auth.consumer_name}`}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Create
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile-friendly list view */}
      <div className="d-block d-md-none">
        {data.map((auth) => (
          <div
            key={auth.authorization_id}
            className="widget-mobile-card widget-mobile-card--danger border bg-light"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="fw-bold">{auth.consumer_name}</div>
                <div className="small text-muted">
                  {auth.authorization_number}
                </div>
                <div className="small">
                  <span className="badge bg-secondary text-capitalize">
                    {auth.auth_type}
                  </span>
                </div>
              </div>
              <button
                className="btn btn-sm btn-primary ms-2"
                onClick={() =>
                  handleCreateReport(auth.authorization_id, auth.consumer_id)
                }
                aria-label={`Create report for ${auth.consumer_name}`}
              >
                <i className="bi bi-plus-circle"></i>
                <span className="d-none d-sm-inline ms-1">Create</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {data.length > 5 && (
        <div className="text-center mt-3">
          <small className="text-muted">
            Showing {data.length} missing reports
          </small>
        </div>
      )}
    </WidgetCard>
  );
};

export default MissingMonthlyReportsWidget;
